import { client } from '../../../config/gql/client';
import { NotificationAlert } from 'core/classes/NotificationAlert';
import { ResponseStatus } from 'core/classes/ResponseStatus';
import { coreActions } from 'core/redux/actions/coreActions';
import { HandleGraphqlRequestAction } from 'core/types/redux/CoreStateActions';
import { isEmpty } from 'lodash';
import { AnyAction } from 'redux';
import { larvaActions } from 'smartHome/larva/redux/actions/larvaActions';
import { requestToRefreshExpiredToken, setLogout } from 'tenantUser/redux/authActions';
import * as yup from 'yup';

export const axiosResponseSchema = yup.object().shape({
    data: yup.mixed().required(),
    status: yup.number().required(),
    statusText: yup.string(),
    headers: yup.object().required(),
    config: yup.object().required(),
    request: yup.mixed().required(),
});

export type GraphQLAxiosResponse = yup.InferType<typeof axiosResponseSchema>;

export const handleGraphQLRequest = async function (action: HandleGraphqlRequestAction): Promise<AnyAction[]> {
    const { query, variables, callbackType, passThroughData } = action.payload;

    if (!query) {
        console.log('%c [WARN]: GQL request cancelled due to the invalid request query', 'color: orange');
        throw new Error('Invalid request query');
    }

    const response = await client.mutate({
        mutation: query,
        variables: variables || {},
    });

    if ('errors' in response.data) {
        console.log('[Error in response]', response.data.errors);
        console.log('[Response]', response);

        // return handleErrorResponse(response, action);
    }

    if ('warnings' in response.data) {
        console.log('[Warnings in response]', response.data.warnings);
        console.log('[Response]', response);
    }

    return [{ type: callbackType, payload: { ...response, ...passThroughData } }];
};

export const handleErrorResponse = (response: GraphQLAxiosResponse, action: AnyAction): AnyAction[] => {
    let alert = new NotificationAlert({
        message: response.data.message || 'notices.criticalError',
        level: NotificationAlert.ERROR,
    });

    if (ResponseStatus.isInvalidRefreshTokenMessage(response?.data?.message)) {
        return [setLogout()];
    }

    if (
        ResponseStatus.isUnauthorizedCode(response?.status) ||
        ResponseStatus.isInvalidAccessTokenMessage(response?.data?.message)
    ) {
        alert = alert.withMessage('One moment, refreshing...').withLevel(NotificationAlert.WARNING);

        return [coreActions.notification.set(alert), requestToRefreshExpiredToken(), larvaActions.queueAction(action)];
    }

    if (/card must be unique/i.test(`${response?.data?.message}`)) {
        alert = alert.withMessage(`notices.card must be unique`);
    }

    if (/PIN is too easy to guess/i.test(`${response?.data?.message}`)) {
        alert = alert.withMessage(`notices.PIN is too easy to guess`);
    }

    if (response.data.errors && !isEmpty(response.data.errors)) {
        for (const text of response.data.errors) {
            alert = alert.withMessage(`${alert.message}, ${text?.message}`);
        }
    }

    return [coreActions.notification.set(alert)];
};

export const b64toBlob = (b64Data: string, mimeType: string, sliceSize = 512): string => {
    // @ts-ignore
    // eslint-disable-next-line no-constant-condition
    const byteChars = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteChars.length; offset += sliceSize) {
        const slice = byteChars.slice(offset, offset + sliceSize);

        const byteNumbers: number[] = Array.from({ length: slice.length });

        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: mimeType });

    // eslint-disable-next-line compat/compat
    return URL.createObjectURL(blob);
};
